// qs-SmoothPanel.vue
<template>
  <div class="smooth-panel">
    <div 
      v-for="(item, index) in smoothingModes" 
      :key="item.id"
      class="smooth-item"
      :class="{ active: selectedIndex === index }"
      :tabindex="0"
      :aria-label="`Select ${item.name} smoothing`"
      role="button"
      @click="selectSmoothingMode(index)"
      @keydown.enter="selectSmoothingMode(index)"
      @keydown.space.prevent="selectSmoothingMode(index)"
      @touchstart.passive="!props.disabled && selectSmoothingMode(index)"
      @mouseleave="handleMouseLeave"
    >
      <div class="preview-area">
        <img
          :src="item.icon"
          :alt="`${item.name} smoothing icon`"
          class="smooth-icon"
          :aria-label="`${item.name} smoothing icon`"
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useToolStore } from '@/stores/qs-store-tools'
import type { SmoothingMode } from '@/types/qs-types-index'
import iconSmoothStraight from '@/assets/smooth-icons/qs-icon-smooth-straight.svg'
import iconSmoothLight from '@/assets/smooth-icons/qs-icon-smooth-light.svg'
import iconSmoothNone from '@/assets/smooth-icons/qs-icon-smooth-none.svg'

interface SmoothingModeItem {
  readonly id: string
  readonly mode: SmoothingMode
  readonly name: string
  readonly description: string
  readonly icon: string
}

interface Props {
  currentSmoothingMode?: SmoothingMode
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currentSmoothingMode: 'curved',
  disabled: false
})

const emit = defineEmits<{
  'smoothing-mode-changed': [mode: SmoothingMode, index: number]
  'smoothing-mode-selected': [mode: SmoothingModeItem]
}>()

const toolStore = useToolStore()

const smoothingModes: readonly SmoothingModeItem[] = [
  {
    id: 'straight',
    mode: 'straight',
    name: 'Straight',
    description: 'Only start and end point - straight line',
    icon: iconSmoothStraight
  },
  {
    id: 'light',
    mode: 'light',
    name: 'Light',
    description: 'Light smoothing with gentle curves',
    icon: iconSmoothLight
  },
  {
    id: 'curved',
    mode: 'curved',
    name: 'Curved',
    description: 'Many curves - current behavior',
    icon: iconSmoothNone
  }
] as const

const selectedIndex = ref(2)
const isInitialized = ref(false)

const findModeIndex = (mode: SmoothingMode | undefined): number => {
  if (!mode) return 2
  const index = smoothingModes.findIndex(sm => sm.mode === mode)
  return index !== -1 ? index : 2
}

const selectSmoothingMode = (index: number): void => {
  if (props.disabled || index < 0 || index >= smoothingModes.length) {
    console.warn('SmoothPanel: Invalid smoothing mode selection', { index, disabled: props.disabled })
    return
  }

  selectedIndex.value = index
  const smoothingMode = smoothingModes[index]
  toolStore.setSmoothingMode(smoothingMode.mode)
  emit('smoothing-mode-changed', smoothingMode.mode, index)
  emit('smoothing-mode-selected', smoothingMode)
}

const handleMouseLeave = (event: MouseEvent): void => {
  const target = event.target as HTMLElement
  if (target?.classList.contains('smooth-item')) {
    target.blur()
  }
}

const updateSelectedIndex = (mode: SmoothingMode | undefined): void => {
  if (mode && isInitialized.value) {
    selectedIndex.value = findModeIndex(mode)
  }
}

watch(() => toolStore.currentTool.smoothingMode, updateSelectedIndex)

watch(() => props.currentSmoothingMode, updateSelectedIndex, { immediate: false })

onMounted(() => {
  try {
    const currentMode = toolStore.currentTool.smoothingMode || 'curved'
    selectedIndex.value = findModeIndex(currentMode)
    isInitialized.value = true
    
    const smoothingMode = smoothingModes[selectedIndex.value]
    emit('smoothing-mode-changed', smoothingMode.mode, selectedIndex.value)
    emit('smoothing-mode-selected', smoothingMode)
  } catch (error) {
    console.error('SmoothPanel: Error during initialization', error)
  }
})
</script>

<style scoped>
.smooth-panel {
  display: flex;
  flex-direction: column;
  width: auto;
  min-width: fit-content;
  background: transparent;
  gap: theme('spacing.1');
  position: relative;
  user-select: none;
}

.smooth-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: theme('spacing.11');
  height: theme('spacing.11');
  background: transparent;
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: background-color 0.15s ease;
  outline: none;
  min-height: theme('spacing.11');
}

.smooth-item:hover:not(:disabled):not(:focus) {
  background: theme('colors.amber.50');
}

.smooth-item:focus:not(:disabled) {
  background: theme('colors.gray.300');
  outline: none;
}

.smooth-item:focus-visible {
  outline: none;
}

.smooth-item.active {
  background: #fcfcfc;
}

.smooth-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.preview-area {
  width: theme('spacing.8');
  height: theme('spacing.8');
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.smooth-icon {
  width: 20px !important;
  height: 20px !important;
  color: theme('colors.gray.800');
  transition: color 0.15s ease;
}

.smooth-item.active .smooth-icon {
  color: theme('colors.gray.800');
}

.smooth-item:hover:not(:disabled) .smooth-icon {
  color: theme('colors.gray.800');
}

@media (hover: none) and (pointer: coarse) {
  .smooth-item {
    min-height: theme('spacing.11');
    padding: theme('spacing.1.5');
  }
  
  .smooth-item:active {
    background: theme('colors.gray.300');
    transform: scale(0.98);
  }
}

@media (prefers-contrast: high) {
  .smooth-item.active {
    background: theme('colors.black');
    color: theme('colors.creme.light');
  }
}

@media (prefers-reduced-motion: reduce) {
  .smooth-item {
    transition: none;
  }
}
</style>